import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import i18n from '../i18n';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract language from URL path (e.g., /en/about -> 'en')
  const getLanguageFromPath = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const langFromPath = pathParts[0];
    
    // Validate language
    if (['en', 'bm', 'zh'].includes(langFromPath)) {
      return langFromPath;
    }
    return null;
  };

  // Get initial language
  const getInitialLanguage = () => {
    // First, check URL
    const langFromPath = getLanguageFromPath();
    if (langFromPath) return langFromPath;
    
    // Then check localStorage
    const savedLang = localStorage.getItem('ewands-language');
    if (savedLang && ['en', 'bm', 'zh'].includes(savedLang)) {
      return savedLang;
    }
    
    // Default to English
    return 'en';
  };

  const [language, setLanguage] = useState(getInitialLanguage);

  // Sync i18n when language changes
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
    localStorage.setItem('ewands-language', language);
    // Set data-lang attribute for CSS styling
    document.documentElement.setAttribute('data-lang', language);
  }, [language]);

  // Update language from URL changes
  useEffect(() => {
    const langFromPath = getLanguageFromPath();
    if (langFromPath && langFromPath !== language) {
      setLanguage(langFromPath);
    }
  }, [location.pathname]);

  const changeLanguage = (newLang) => {
    if (!['en', 'bm', 'zh'].includes(newLang)) {
      console.error('Invalid language:', newLang);
      return;
    }

    // Get current path without language prefix
    const pathParts = location.pathname.split('/').filter(Boolean);
    const currentLang = getLanguageFromPath();
    const pathWithoutLang = currentLang 
      ? '/' + pathParts.slice(1).join('/')
      : location.pathname;

    // Navigate to new language path
    const newPath = `/${newLang}${pathWithoutLang || ''}`;
    navigate(newPath + location.search + location.hash);
    
    setLanguage(newLang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
